import { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { useNotifications } from '../hooks/useNotifications';
import { 
  Clock, 
  Play,
  Pause,
  Coffee,
  Moon,
  Truck,
  AlertCircle,
  Calendar,
  RotateCcw,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import { formatDuration, getTodayDate, getCurrentTime } from '../lib/utils';
import { toast } from 'sonner';

const statusOptions = [
  { id: 'driving', label: 'Conduite', icon: Truck, color: 'bg-amber-500' },
  { id: 'on_duty', label: 'En service', icon: Clock, color: 'bg-blue-500' },
  { id: 'off_duty', label: 'Repos', icon: Coffee, color: 'bg-slate-600' },
  { id: 'sleeper', label: 'Couchette', icon: Moon, color: 'bg-purple-500' }
];

// Legal limits (in minutes) -  / Canadian regulations
const LIMITS = {
  // Daily limits
  maxDriving: 13 * 60,      // 13 hours max driving per day
  maxOnDuty: 14 * 60,       // 14 hours max on duty per day
  minOffDuty: 10 * 60,      // 10 hours min off duty per day
  minConsecutiveRest: 8 * 60, // 8 hours consecutive rest required
  
  // Cycle limits
  cycle1: {
    days: 7,
    maxHours: 70 * 60,      // 70 hours per 7 days
    resetHours: 36          // 36 consecutive hours to reset
  },
  cycle2: {
    days: 14,
    maxHours: 120 * 60,     // 120 hours per 14 days
    resetHours: 72          // 72 consecutive hours to reset
  }
};

export const ServiceHoursPage = () => {
  const { get, post, patch } = useApi();
  const { checkHoursAndNotify, permission } = useNotifications();
  const [currentStatus, setCurrentStatus] = useState(null);
  const [activeEntry, setActiveEntry] = useState(null);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Cycle tracking
  const [selectedCycle, setSelectedCycle] = useState('cycle1'); // 'cycle1' or 'cycle2'
  const [cycleData, setCycleData] = useState(null);
  const [showCycleSelector, setShowCycleSelector] = useState(false);

  useEffect(() => {
    fetchData();
    fetchCycleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCycle]);

  // Timer for active entry
  useEffect(() => {
    if (!activeEntry) return;

    const startTime = new Date(`${activeEntry.date}T${activeEntry.start_time}`);
    const updateElapsed = () => {
      const now = new Date();
      const elapsed = Math.floor((now - startTime) / 60000); // minutes
      setElapsedTime(elapsed);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [activeEntry]);

  const fetchData = async () => {
    try {
      const today = getTodayDate();
      
      // Fetch today's entries
      const entriesResult = await get('/service-hours', { date: today });
      if (entriesResult.success) {
        setEntries(entriesResult.data);
        
        // Find active entry (no end_time)
        const active = entriesResult.data.find(e => !e.end_time);
        if (active) {
          setActiveEntry(active);
          setCurrentStatus(active.status);
        }
      }

      // Fetch summary
      const summaryResult = await get(`/service-hours/summary/${today}`);
      if (summaryResult.success) {
        setSummary(summaryResult.data);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Fetch cycle data (last 7 or 14 days) - uses backend endpoint with reset logic
  const fetchCycleData = async () => {
    try {
      const cycleDays = selectedCycle === 'cycle1' ? LIMITS.cycle1.days : LIMITS.cycle2.days;
      
      const result = await get(`/service-hours/cycle?cycle_days=${cycleDays}`);
      if (result.success && result.data) {
        const data = result.data;
        setCycleData({
          totalWorkMin: data.total_work_min || 0,
          remainingMin: data.remaining_min || 0,
          cycleLimit: data.max_hours_min || (selectedCycle === 'cycle1' ? LIMITS.cycle1.maxHours : LIMITS.cycle2.maxHours),
          cycleDays: data.cycle_days || cycleDays,
          dailyData: (data.daily_data || []).map(d => ({
            date: d.date,
            workMin: d.work_min || 0,
            drivingMin: d.driving_min || 0,
            onDutyMin: d.on_duty_min || 0
          })),
          resetHours: data.reset_hours || (selectedCycle === 'cycle1' ? LIMITS.cycle1.resetHours : LIMITS.cycle2.resetHours),
          lastReset: data.last_reset || null
        });
      }
    } catch (err) {
      console.error('Error fetching cycle data:', err);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const now = getCurrentTime();
    const today = getTodayDate();

    try {
      // End current entry if exists
      if (activeEntry) {
        // Calculate duration
        const startTime = new Date(`${today}T${activeEntry.start_time}`);
        const endTime = new Date(`${today}T${now}`);
        const duration = Math.floor((endTime - startTime) / 60000);

        await patch(`/service-hours/${activeEntry.id}`, null, {
          params: { end_time: now, duration_min: duration }
        });
      }

      // Create new entry
      const result = await post('/service-hours', {
        date: today,
        status: newStatus,
        start_time: now
      });

      if (result.success) {
        setActiveEntry(result.data);
        setCurrentStatus(newStatus);
        setElapsedTime(0);
        toast.success(`Statut changé: ${statusOptions.find(s => s.id === newStatus)?.label}`);
        fetchData();
      }
    } catch (err) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleStopTracking = async () => {
    if (!activeEntry) return;

    const now = getCurrentTime();
    const today = getTodayDate();
    const startTime = new Date(`${today}T${activeEntry.start_time}`);
    const endTime = new Date(`${today}T${now}`);
    const duration = Math.floor((endTime - startTime) / 60000);

    try {
      await patch(`/service-hours/${activeEntry.id}`, null, {
        params: { end_time: now, duration_min: duration }
      });
      
      setActiveEntry(null);
      setCurrentStatus(null);
      toast.success('Suivi arrêté');
      fetchData();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const getProgressPercentage = (current, max) => {
    return Math.min((current / max) * 100, 100);
  };

  const isOverLimit = (current, max) => current >= max;

  // Calculer les heures (utilisé aussi pour les notifications)
  const drivingMin = (summary?.driving_min || 0) + (currentStatus === 'driving' ? elapsedTime : 0);
  const onDutyMin = (summary?.on_duty_min || 0) + (currentStatus === 'on_duty' ? elapsedTime : 0);
  const totalWorkMin = drivingMin + onDutyMin;

  // Vérifier les limites et envoyer des notifications si nécessaire
  useEffect(() => {
    if (permission === 'granted' && !loading && summary) {
      checkHoursAndNotify({
        drivingMin,
        totalWorkMin,
        cycleRemainingMin: cycleData?.remainingMin
      });
    }
  }, [drivingMin, totalWorkMin, cycleData?.remainingMin, permission, loading, summary, checkHoursAndNotify]);

  if (loading) {
    return (
      <div className="page-wrapper p-4" data-testid="hours-loading">
        <div className="space-y-4">
          <div className="h-32 bg-slate-800 rounded-xl animate-pulse" />
          <div className="h-48 bg-slate-800 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper" data-testid="hours-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center gap-3">
          <Clock className="text-amber-500" size={28} />
          <div>
            <h1 className="text-2xl text-white">Heures facturables</h1>
            <p className="text-slate-400 text-sm">
              {new Date().toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Current Status */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid="current-status">
          <p className="label-uppercase mb-3">Statut actuel</p>
          {currentStatus ? (
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-xl ${statusOptions.find(s => s.id === currentStatus)?.color} flex items-center justify-center`}>
                {(() => {
                  const StatusIcon = statusOptions.find(s => s.id === currentStatus)?.icon || Clock;
                  return <StatusIcon className="text-white" size={32} />;
                })()}
              </div>
              <div className="flex-1">
                <p className="text-xl font-bold text-white">
                  {statusOptions.find(s => s.id === currentStatus)?.label}
                </p>
                <p className="text-amber-500 text-lg font-mono">
                  {formatDuration(elapsedTime)}
                </p>
              </div>
              <Button
                onClick={handleStopTracking}
                variant="outline"
                className="h-14 px-4 border-slate-700"
                data-testid="stop-tracking"
              >
                <Pause size={20} />
              </Button>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-slate-400">Aucun statut actif</p>
              <p className="text-sm text-slate-500 mt-1">Sélectionnez un statut ci-dessous</p>
            </div>
          )}
        </div>

        {/* Status Buttons */}
        <div className="grid grid-cols-2 gap-3" data-testid="status-buttons">
          {statusOptions.map(option => {
            const Icon = option.icon;
            const isActive = currentStatus === option.id;
            
            return (
              <button
                key={option.id}
                onClick={() => handleStatusChange(option.id)}
                className={`p-4 rounded-xl border-2 transition-all active:scale-95 ${
                  isActive 
                    ? `${option.color} border-transparent` 
                    : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                }`}
                data-testid={`status-${option.id}`}
              >
                <Icon className={isActive ? 'text-white' : 'text-slate-400'} size={28} />
                <p className={`mt-2 font-bold ${isActive ? 'text-white' : 'text-slate-300'}`}>
                  {option.label}
                </p>
              </button>
            );
          })}
        </div>

        {/* Hours Summary */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid="hours-summary">
          <p className="label-uppercase mb-4">Résumé du jour</p>
          
          {/* Driving Hours */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm font-semibold">Conduite</span>
              <span className={`font-mono font-bold ${isOverLimit(drivingMin, LIMITS.maxDriving) ? 'text-red-500' : 'text-white'}`}>
                {formatDuration(drivingMin)} / {formatDuration(LIMITS.maxDriving)}
              </span>
            </div>
            <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${isOverLimit(drivingMin, LIMITS.maxDriving) ? 'bg-red-500' : 'bg-amber-500'}`}
                style={{ width: `${getProgressPercentage(drivingMin, LIMITS.maxDriving)}%` }}
              />
            </div>
          </div>

          {/* On Duty Hours */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm font-semibold">Total service</span>
              <span className={`font-mono font-bold ${isOverLimit(totalWorkMin, LIMITS.maxOnDuty) ? 'text-red-500' : 'text-white'}`}>
                {formatDuration(totalWorkMin)} / {formatDuration(LIMITS.maxOnDuty)}
              </span>
            </div>
            <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${isOverLimit(totalWorkMin, LIMITS.maxOnDuty) ? 'bg-red-500' : 'bg-blue-500'}`}
                style={{ width: `${getProgressPercentage(totalWorkMin, LIMITS.maxOnDuty)}%` }}
              />
            </div>
          </div>

          {/* Visual Timeline */}
          <div className="hours-timeline mt-6">
            {drivingMin > 0 && (
              <div 
                className="hours-driving hours-segment"
                style={{ width: `${(drivingMin / (LIMITS.maxOnDuty)) * 100}%` }}
              >
                {drivingMin > 60 && <span>{Math.floor(drivingMin / 60)}h</span>}
              </div>
            )}
            {onDutyMin > 0 && (
              <div 
                className="hours-on-duty hours-segment"
                style={{ width: `${(onDutyMin / (LIMITS.maxOnDuty)) * 100}%` }}
              >
                {onDutyMin > 60 && <span>{Math.floor(onDutyMin / 60)}h</span>}
              </div>
            )}
            {(summary?.off_duty_min || 0) > 0 && (
              <div 
                className="hours-off-duty hours-segment"
                style={{ width: `${((summary?.off_duty_min || 0) / (LIMITS.maxOnDuty)) * 100}%` }}
              />
            )}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <span className="text-xs text-slate-400">Conduite</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-xs text-slate-400">Service</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-600" />
              <span className="text-xs text-slate-400">Repos</span>
            </div>
          </div>
        </div>

        {/* ========== CYCLE RECAP SECTION () ========== */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid="cycle-recap">
          {/* Cycle Selector */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="text-green-500" size={20} />
              <span className="label-uppercase">Récap cycle </span>
            </div>
            <button
              onClick={() => setShowCycleSelector(!showCycleSelector)}
              className="flex items-center gap-2 bg-slate-800 px-3 py-2 rounded-lg text-sm text-white hover:bg-slate-700"
              data-testid="cycle-selector"
            >
              {selectedCycle === 'cycle1' ? 'Cycle 1 (7 jours)' : 'Cycle 2 (14 jours)'}
              <ChevronDown size={16} className={`transition-transform ${showCycleSelector ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Cycle selector dropdown */}
          {showCycleSelector && (
            <div className="mb-4 p-2 bg-slate-800 rounded-lg space-y-2">
              <button
                onClick={() => { setSelectedCycle('cycle1'); setShowCycleSelector(false); }}
                className={`w-full p-3 rounded-lg text-left ${selectedCycle === 'cycle1' ? 'bg-green-600' : 'hover:bg-slate-700'}`}
              >
                <p className="font-bold text-white">Cycle 1 - 7 jours</p>
                <p className="text-xs text-slate-300">Max 70h • Remise à zéro: 36h repos</p>
              </button>
              <button
                onClick={() => { setSelectedCycle('cycle2'); setShowCycleSelector(false); }}
                className={`w-full p-3 rounded-lg text-left ${selectedCycle === 'cycle2' ? 'bg-green-600' : 'hover:bg-slate-700'}`}
              >
                <p className="font-bold text-white">Cycle 2 - 14 jours</p>
                <p className="text-xs text-slate-300">Max 120h • Remise à zéro: 72h repos</p>
              </button>
            </div>
          )}

          {cycleData && (
            <>
              {/* Cycle Progress */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 uppercase">Heures travaillées</p>
                  <p className="text-2xl font-bold text-white font-mono">
                    {formatDuration(cycleData.totalWorkMin)}
                  </p>
                  <p className="text-xs text-slate-400">
                    sur {cycleData.cycleDays} jours
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 uppercase">Heures restantes</p>
                  <p className={`text-2xl font-bold font-mono ${cycleData.remainingMin < 5 * 60 ? 'text-red-500' : cycleData.remainingMin < 10 * 60 ? 'text-amber-500' : 'text-green-500'}`}>
                    {formatDuration(cycleData.remainingMin)}
                  </p>
                  <p className="text-xs text-slate-400">
                    max {formatDuration(cycleData.cycleLimit)}
                  </p>
                </div>
              </div>

              {/* Cycle Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-sm">Utilisation du cycle</span>
                  <span className="text-sm font-mono text-white">
                    {Math.round((cycleData.totalWorkMin / cycleData.cycleLimit) * 100)}%
                  </span>
                </div>
                <div className="h-4 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${
                      cycleData.totalWorkMin >= cycleData.cycleLimit * 0.9 
                        ? 'bg-red-500' 
                        : cycleData.totalWorkMin >= cycleData.cycleLimit * 0.75 
                          ? 'bg-amber-500' 
                          : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min((cycleData.totalWorkMin / cycleData.cycleLimit) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {/* Reset Info */}
              <div className="flex items-center gap-2 p-3 bg-slate-800/30 rounded-lg">
                <RotateCcw className="text-blue-400" size={18} />
                <div className="text-sm">
                  <span className="text-slate-400">Remise à zéro: </span>
                  <span className="text-white font-medium">{cycleData.resetHours}h de repos consécutives</span>
                </div>
              </div>
            </>
          )}

          {!cycleData && (
            <div className="text-center py-4">
              <div className="w-6 h-6 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin mx-auto" />
              <p className="text-slate-400 text-sm mt-2">Chargement du cycle...</p>
            </div>
          )}
        </div>

        {/* Warning if close to limit */}
        {(drivingMin >= LIMITS.maxDriving * 0.9 || totalWorkMin >= LIMITS.maxOnDuty * 0.9) && (
          <div 
            className="bg-amber-900/30 border border-amber-800 rounded-xl p-4 flex items-start gap-3"
            data-testid="limit-warning"
          >
            <AlertCircle className="text-amber-500 flex-shrink-0 mt-1" size={24} />
            <div>
              <p className="text-amber-400 font-bold">Limite journalière proche</p>
              <p className="text-amber-300 text-sm mt-1">
                Vous approchez de la limite légale d'heures de service. Planifiez votre repos.
              </p>
            </div>
          </div>
        )}

        {/* Warning if close to cycle limit */}
        {cycleData && cycleData.remainingMin < 10 * 60 && (
          <div 
            className="bg-red-900/30 border border-red-800 rounded-xl p-4 flex items-start gap-3"
            data-testid="cycle-limit-warning"
          >
            <AlertCircle className="text-red-500 flex-shrink-0 mt-1" size={24} />
            <div>
              <p className="text-red-400 font-bold">Limite de cycle proche!</p>
              <p className="text-red-300 text-sm mt-1">
                Il vous reste moins de 10h sur votre {selectedCycle === 'cycle1' ? 'cycle de 7 jours' : 'cycle de 14 jours'}. 
                Prévoyez {cycleData.resetHours}h de repos consécutives pour remettre vos heures à zéro.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceHoursPage;
